import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import { PageHeader } from "../PageHeader";
import { useIsPhone } from "../../hooks/useIsPhone";

interface FormPageLayoutProps {
  title: string;
  children: ReactNode;
  onSubmit: () => void;
  /** Firestore or server error to display above the form. */
  error: string | null;
  submitting: boolean;
  /** Button label. Defaults to "Save". */
  submitLabel?: string;
  /** When true, hides the bottom Cancel / Submit row (caller renders its own). */
  hideBottomActions?: boolean;
  /** Optional action node rendered to the right of the title (forwarded to PageHeader). */
  headerAction?: ReactNode;
  /**
   * Stable slug for E2E selectors. When set:
   *   - the `<form>` wrapper gets `data-testid="{slug}-form"`
   *   - the submit button gets `data-testid="{slug}-form-submit"`
   *   - the cancel button gets `data-testid="{slug}-form-cancel"`
   *
   * Callers pass kebab-case slugs like "location", "group", "travel",
   * "session" so test IDs read as `location-form-submit` etc.
   */
  testIdSlug?: string;
  /**
   * Phone only: an extra button for the sticky bottom bar, between Cancel
   * and Submit (e.g. a Preview button that opens a preview sheet).
   */
  phoneBarAction?: ReactNode;
}

export function FormPageLayout({
  title,
  children,
  onSubmit,
  error,
  submitting,
  submitLabel = "Save",
  hideBottomActions = false,
  headerAction,
  testIdSlug,
  phoneBarAction,
}: FormPageLayoutProps) {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const formTestId = testIdSlug ? `${testIdSlug}-form` : undefined;
  const submitTestId = testIdSlug ? `${testIdSlug}-form-submit` : undefined;
  const cancelTestId = testIdSlug ? `${testIdSlug}-form-cancel` : undefined;

  if (isPhone) {
    // Phone: tighter padding, and Cancel / Submit in a bar that sticks to
    // the bottom of the screen so saving never needs a scroll to the end.
    // An empty title (session edit, which renders its own header and tabs
    // above the form) would leave a blank heading row; skip it on phones.
    return (
      <Box>
        {(title || headerAction) && (
          <PageHeader title={title} action={headerAction} />
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2 }}>
          <Box
            component="form"
            onSubmit={onSubmit}
            noValidate
            data-testid={formTestId}
          >
            {children}

            {!hideBottomActions && (
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mx: -2,
                  mb: -2,
                  mt: 3,
                  px: 2,
                  py: 1.5,
                  bgcolor: "background.paper",
                  borderTop: 1,
                  borderColor: "divider",
                  borderBottomLeftRadius: "inherit",
                  borderBottomRightRadius: "inherit",
                }}
              >
                <Button
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                  data-testid={cancelTestId}
                >
                  Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                {phoneBarAction}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  startIcon={
                    submitting ? <CircularProgress size={18} /> : undefined
                  }
                  data-testid={submitTestId}
                >
                  {submitting ? "Saving..." : submitLabel}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={title} action={headerAction} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box
          component="form"
          onSubmit={onSubmit}
          noValidate
          data-testid={formTestId}
        >
          {children}

          {!hideBottomActions && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 4,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button
                onClick={() => navigate(-1)}
                disabled={submitting}
                data-testid={cancelTestId}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={18} /> : undefined
                }
                data-testid={submitTestId}
              >
                {submitting ? "Saving..." : submitLabel}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
